/**
 * AuditFlow API client — all requests to the Node.js backend.
 * Attaches the Supabase auth token for user authentication.
 */

import { supabase } from "@/lib/supabase";
import {
    API_BASE_URL,
    isMisconfiguredProductionApi,
    misconfiguredProductionApiMessage,
} from "@/lib/env";
import type {
    AssistantEvent,
    AuditFlowChat,
    AuditFlowChatDetailOut,
    AuditFlowCitationAnnotation,
    AuditFlowDocument,
    AuditFlowFolder,
    AuditFlowMessage,
    AuditFlowProject,
    AuditFlowWorkflow,
    LauraAudit,
    LauraAuditDocument,
    LauraDeliverable,
    LauraP0Context,
    LauraP0Question,
    LauraP0State,
    LauraSAIProfile,
    LauraStageCompletion,
    LauraStageId,
    TabularReview,
    TabularReviewDetailOut,
} from "@/app/components/shared/types";

// Server-side shape before mapping
interface ServerMessage {
    id: string;
    chat_id: string;
    role: "user" | "assistant";
    content: string | AssistantEvent[] | null;
    files?: { filename: string; document_id?: string }[] | null;
    workflow?: { id: string; title: string } | null;
    annotations?: AuditFlowCitationAnnotation[] | null;
    created_at: string;
}
interface ServerChatDetailOut {
    chat: AuditFlowChat;
    messages: ServerMessage[];
}

const API_BASE = API_BASE_URL;

async function getAuthHeader(): Promise<Record<string, string>> {
    const {
        data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) return {};
    return { Authorization: `Bearer ${session.access_token}` };
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
    if (isMisconfiguredProductionApi()) {
        throw new Error(misconfiguredProductionApiMessage());
    }

    const authHeaders = await getAuthHeader();
    const { headers: initHeaders, ...restInit } = init ?? {};
    const response = await fetch(`${API_BASE}${path}`, {
        cache: "no-store",
        ...restInit,
        headers: {
            Accept: "application/json",
            ...authHeaders,
            ...(initHeaders as Record<string, string> | undefined),
        },
    });

    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `API error: ${response.status}`);
    }

    if (
        response.status === 204 ||
        response.headers.get("content-length") === "0"
    ) {
        return undefined as T;
    }

    return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// PDF Viewer
// ---------------------------------------------------------------------------

/**
 * Get a signed download URL for the EmbedPDF viewer.
 */
export async function getSignedDisplayUrl(
    documentId: string,
    versionId?: string | null,
): Promise<{ url: string }> {
    const qs = versionId
        ? `?version_id=${encodeURIComponent(versionId)}`
        : "";
    return apiRequest<{ url: string }>(
        `/single-documents/${documentId}/display-url${qs}`,
        { method: "POST" },
    );
}

// ---------------------------------------------------------------------------
// PDF Annotations (EmbedPDF)
// ---------------------------------------------------------------------------

export async function getPdfAnnotations(
    documentId: string,
    versionId?: string | null,
): Promise<{ annotations: import("@embedpdf/react-pdf-viewer").AnnotationTransferItem[] }> {
    const qs = versionId
        ? `?version_id=${encodeURIComponent(versionId)}`
        : "";
    const result = await apiRequest<{ annotations: unknown[] }>(
        `/single-documents/${documentId}/annotations${qs}`,
    );
    return {
        annotations: (result.annotations ?? []) as import("@embedpdf/react-pdf-viewer").AnnotationTransferItem[],
    };
}

export async function savePdfAnnotationsJson(
    documentId: string,
    annotations: unknown[],
    versionId?: string | null,
): Promise<void> {
    const qs = versionId
        ? `?version_id=${encodeURIComponent(versionId)}`
        : "";
    return apiRequest<void>(
        `/single-documents/${documentId}/annotations${qs}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ annotations }),
        },
    );
}

// ---------------------------------------------------------------------------
// Suggestion Chips (ChatPDF-style)
// ---------------------------------------------------------------------------

export async function getSuggestedQuestions(
    projectId: string,
    documentId: string,
): Promise<{ suggestions: string[] }> {
    return apiRequest<{ suggestions: string[] }>(
        `/projects/${projectId}/chat/suggest-questions`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ document_id: documentId }),
        },
    );
}

// ---------------------------------------------------------------------------
// Legacy PDF Annotations (baked into PDF bytes via pdf-lib)
// ---------------------------------------------------------------------------

/**
 * Overwrite the current version's PDF file in R2 with annotated bytes.
 * Only works for native PDF documents (file_type = 'pdf').
 */
export async function savePdfAnnotations(
    documentId: string,
    pdfBytes: Uint8Array,
): Promise<void> {
    const authHeaders = await getAuthHeader();
    const response = await fetch(
        `${API_BASE}/single-documents/${documentId}/pdf-bytes`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/pdf",
                ...authHeaders,
            },
            body: pdfBytes.buffer.slice(
                pdfBytes.byteOffset,
                pdfBytes.byteOffset + pdfBytes.byteLength,
            ) as ArrayBuffer,
            cache: "no-store",
        },
    );
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `API error: ${response.status}`);
    }
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export async function listProjects(): Promise<AuditFlowProject[]> {
    return apiRequest<AuditFlowProject[]>("/projects");
}

export async function createProject(
    name: string,
    cm_number?: string,
    shared_with?: string[],
): Promise<AuditFlowProject> {
    return apiRequest<AuditFlowProject>("/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, cm_number, shared_with }),
    });
}

export async function deleteAccount(): Promise<void> {
    return apiRequest<void>("/user/account", { method: "DELETE" });
}

export interface UserProfile {
    displayName: string | null;
    organisation: string | null;
    messageCreditsUsed: number;
    creditsResetDate: string;
    creditsRemaining: number;
    tier: string;
    tabularModel: string;
    chatModel: string;
    suggestionModel: string;
    suggestionPromptTemplate: string;
    apiKeyStatus?: ApiKeyStatus;
}

export async function getUserProfile(): Promise<UserProfile> {
    return apiRequest<UserProfile>("/user/profile");
}

export async function updateUserProfile(payload: {
    displayName?: string | null;
    organisation?: string | null;
    tabularModel?: string;
    chatModel?: string;
    suggestionModel?: string;
    suggestionPromptTemplate?: string;
}): Promise<UserProfile> {
    return apiRequest<UserProfile>("/user/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export type ApiKeyProvider =
    | "claude"
    | "gemini"
    | "openai"
    | "kimi"
    | "openrouter";
export type ApiKeySource = "user" | "env" | null;
export type ApiKeyState = Record<
    ApiKeyProvider,
    {
        configured: boolean;
        source: ApiKeySource;
    }
>;

export type ApiKeyStatus = Record<ApiKeyProvider, boolean> & {
    sources?: Partial<Record<ApiKeyProvider, ApiKeySource>>;
};

export async function getApiKeyStatus(): Promise<ApiKeyStatus> {
    return apiRequest<ApiKeyStatus>("/user/api-keys");
}

export async function saveApiKey(
    provider: ApiKeyProvider,
    apiKey: string | null,
): Promise<ApiKeyStatus> {
    return apiRequest<ApiKeyStatus>(`/user/api-keys/${provider}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ api_key: apiKey }),
    });
}

export async function getProject(projectId: string): Promise<AuditFlowProject> {
    return apiRequest<AuditFlowProject>(`/projects/${projectId}`);
}

export async function updateProject(
    projectId: string,
    payload: {
        name?: string;
        cm_number?: string;
        shared_with?: string[];
    },
): Promise<AuditFlowProject> {
    return apiRequest<AuditFlowProject>(`/projects/${projectId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function deleteProject(projectId: string): Promise<void> {
    await apiRequest(`/projects/${projectId}`, { method: "DELETE" });
}

export interface ProjectPeople {
    owner: {
        user_id: string;
        email: string | null;
        display_name: string | null;
    };
    members: { email: string; display_name: string | null }[];
}

export async function getProjectPeople(
    projectId: string,
): Promise<ProjectPeople> {
    return apiRequest<ProjectPeople>(`/projects/${projectId}/people`);
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export async function createProjectFolder(
    projectId: string,
    name: string,
    parentFolderId?: string | null,
): Promise<AuditFlowFolder> {
    return apiRequest<AuditFlowFolder>(`/projects/${projectId}/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            name,
            parent_folder_id: parentFolderId ?? null,
        }),
    });
}

export async function renameProjectFolder(
    projectId: string,
    folderId: string,
    name: string,
): Promise<AuditFlowFolder> {
    return apiRequest<AuditFlowFolder>(
        `/projects/${projectId}/folders/${folderId}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
        },
    );
}

export async function deleteProjectFolder(
    projectId: string,
    folderId: string,
): Promise<void> {
    await apiRequest(`/projects/${projectId}/folders/${folderId}`, {
        method: "DELETE",
    });
}

export async function moveSubfolderToFolder(
    projectId: string,
    folderId: string,
    parentFolderId: string | null,
): Promise<AuditFlowFolder> {
    return apiRequest<AuditFlowFolder>(
        `/projects/${projectId}/folders/${folderId}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ parent_folder_id: parentFolderId }),
        },
    );
}

export async function moveDocumentToFolder(
    projectId: string,
    documentId: string,
    folderId: string | null,
): Promise<AuditFlowDocument> {
    return apiRequest<AuditFlowDocument>(
        `/projects/${projectId}/documents/${documentId}/folder`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ folder_id: folderId }),
        },
    );
}

export async function renameProjectDocument(
    projectId: string,
    documentId: string,
    filename: string,
): Promise<AuditFlowDocument> {
    return apiRequest<AuditFlowDocument>(
        `/projects/${projectId}/documents/${documentId}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename }),
        },
    );
}

export async function addDocumentToProject(
    projectId: string,
    documentId: string,
): Promise<AuditFlowDocument> {
    return apiRequest<AuditFlowDocument>(
        `/projects/${projectId}/documents/${documentId}`,
        { method: "POST" },
    );
}

export interface AuditFlowDocumentVersion {
    id: string;
    version_number: number | null;
    source: string;
    created_at: string;
    display_name: string | null;
}

export async function listDocumentVersions(documentId: string): Promise<{
    current_version_id: string | null;
    versions: AuditFlowDocumentVersion[];
}> {
    return apiRequest(`/single-documents/${documentId}/versions`);
}

export async function uploadDocumentVersion(
    documentId: string,
    file: File,
    displayName?: string,
): Promise<AuditFlowDocumentVersion> {
    const authHeaders = await getAuthHeader();
    const form = new FormData();
    form.append("file", file);
    if (displayName) form.append("display_name", displayName);
    const response = await fetch(
        `${API_BASE}/single-documents/${documentId}/versions`,
        {
            method: "POST",
            headers: { ...authHeaders },
            body: form,
        },
    );
    if (!response.ok) throw new Error(await response.text());
    return response.json() as Promise<AuditFlowDocumentVersion>;
}

export async function renameDocumentVersion(
    documentId: string,
    versionId: string,
    displayName: string | null,
): Promise<AuditFlowDocumentVersion> {
    return apiRequest<AuditFlowDocumentVersion>(
        `/single-documents/${documentId}/versions/${versionId}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ display_name: displayName }),
        },
    );
}

export async function uploadProjectDocument(
    projectId: string,
    file: File,
): Promise<AuditFlowDocument> {
    const authHeaders = await getAuthHeader();
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(
        `${API_BASE}/projects/${projectId}/documents`,
        {
            method: "POST",
            headers: { ...authHeaders },
            body: form,
        },
    );
    if (!response.ok) throw new Error(await response.text());
    return response.json() as Promise<AuditFlowDocument>;
}

export async function uploadStandaloneDocument(
    file: File,
): Promise<AuditFlowDocument> {
    const authHeaders = await getAuthHeader();
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(`${API_BASE}/single-documents`, {
        method: "POST",
        headers: { ...authHeaders },
        body: form,
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json() as Promise<AuditFlowDocument>;
}

export async function listStandaloneDocuments(): Promise<AuditFlowDocument[]> {
    return apiRequest<AuditFlowDocument[]>("/single-documents");
}

export async function deleteDocument(documentId: string): Promise<void> {
    await apiRequest(`/single-documents/${documentId}`, { method: "DELETE" });
}

export async function getDocumentUrl(
    documentId: string,
    versionId?: string | null,
): Promise<{ url: string; filename: string; version_id: string | null }> {
    const qs = versionId ? `?version_id=${encodeURIComponent(versionId)}` : "";
    return apiRequest(`/single-documents/${documentId}/url${qs}`);
}

export async function downloadDocumentsZip(
    documentIds: string[],
): Promise<Blob> {
    const authHeaders = await getAuthHeader();
    const response = await fetch(`${API_BASE}/single-documents/download-zip`, {
        method: "POST",
        cache: "no-store",
        headers: {
            "Content-Type": "application/json",
            ...authHeaders,
        },
        body: JSON.stringify({ document_ids: documentIds }),
    });
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `API error: ${response.status}`);
    }
    return response.blob();
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export async function createChat(payload?: {
    project_id?: string;
}): Promise<{ id: string }> {
    return apiRequest<{ id: string }>("/chat/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload ?? {}),
    });
}

export async function listChats(): Promise<AuditFlowChat[]> {
    return apiRequest<AuditFlowChat[]>("/chat");
}

export async function listProjectChats(projectId: string): Promise<AuditFlowChat[]> {
    return apiRequest<AuditFlowChat[]>(`/projects/${projectId}/chats`);
}

export async function getChat(chatId: string): Promise<AuditFlowChatDetailOut> {
    const raw = await apiRequest<ServerChatDetailOut>(`/chat/${chatId}`);
    const messages: AuditFlowMessage[] = raw.messages.map((m) => {
        if (m.role === "user") {
            return {
                role: "user",
                content: typeof m.content === "string" ? m.content : "",
                files: m.files ?? undefined,
                workflow: m.workflow ?? undefined,
            };
        }
        const events = Array.isArray(m.content)
            ? (m.content as AssistantEvent[])
            : undefined;
        return {
            role: "assistant",
            content:
                events
                    ?.filter((e) => e.type === "content")
                    .map((e) => (e as { type: "content"; text: string }).text)
                    .join("") ?? "",
            annotations: m.annotations ?? undefined,
            events,
        };
    });
    return { chat: raw.chat, messages };
}

export async function renameChat(chatId: string, title: string): Promise<void> {
    await apiRequest(`/chat/${chatId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
    });
}

export async function deleteChat(chatId: string): Promise<void> {
    await apiRequest(`/chat/${chatId}`, { method: "DELETE" });
}

export async function generateChatTitle(
    chatId: string,
    message: string,
): Promise<{ title: string }> {
    return apiRequest<{ title: string }>(`/chat/${chatId}/generate-title`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
    });
}

export async function streamChat(payload: {
    messages: {
        role: string;
        content: string;
        files?: { filename: string; document_id?: string }[];
        workflow?: { id: string; title: string };
    }[];
    chat_id?: string;
    project_id?: string;
    model?: string;
    signal?: AbortSignal;
}): Promise<Response> {
    const { signal, ...body } = payload;
    const authHeaders = await getAuthHeader();
    return fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...authHeaders,
        },
        body: JSON.stringify(body),
        signal,
    });
}

type StreamChatMessage = {
    role: string;
    content: string;
    files?: { filename: string; document_id?: string }[];
    workflow?: { id: string; title: string };
};

export async function streamProjectChat(payload: {
    projectId: string;
    messages: StreamChatMessage[];
    chat_id?: string;
    model?: string;
    displayed_doc?: { filename: string; document_id: string };
    attached_documents?: { filename: string; document_id: string }[];
    signal?: AbortSignal;
}): Promise<Response> {
    const { projectId, signal, ...body } = payload;
    const authHeaders = await getAuthHeader();
    return fetch(`${API_BASE}/projects/${projectId}/chat`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...authHeaders,
        },
        body: JSON.stringify(body),
        signal,
    });
}

// ---------------------------------------------------------------------------
// Tabular Review
// ---------------------------------------------------------------------------

export async function listTabularReviews(
    projectId?: string,
): Promise<TabularReview[]> {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
    return apiRequest<TabularReview[]>(`/tabular-review${qs}`);
}

export async function createTabularReview(payload: {
    title?: string;
    document_ids: string[];
    columns_config: { index: number; name: string; prompt: string }[];
    workflow_id?: string;
    project_id?: string;
}): Promise<TabularReview> {
    return apiRequest<TabularReview>("/tabular-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function getTabularReview(
    reviewId: string,
): Promise<TabularReviewDetailOut> {
    return apiRequest<TabularReviewDetailOut>(`/tabular-review/${reviewId}`);
}

export async function updateTabularReview(
    reviewId: string,
    payload: {
        title?: string;
        columns_config?: { index: number; name: string; prompt: string }[];
        document_ids?: string[];
        project_id?: string | null;
        shared_with?: string[];
        reviewed_by?: string | null;
        reviewed_at?: string | null;
        locked_by?: string | null;
        locked_at?: string | null;
    },
): Promise<TabularReview> {
    return apiRequest<TabularReview>(`/tabular-review/${reviewId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function getTabularReviewPeople(
    reviewId: string,
): Promise<ProjectPeople> {
    return apiRequest<ProjectPeople>(`/tabular-review/${reviewId}/people`);
}

export async function generateTabularColumnPrompt(
    title: string,
    options?: { format?: string; documentName?: string; tags?: string[] },
): Promise<{ prompt: string; source: "preset" | "llm" | "fallback" }> {
    return apiRequest<{
        prompt: string;
        source: "preset" | "llm" | "fallback";
    }>("/tabular-review/prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            title,
            format: options?.format,
            documentName: options?.documentName,
            tags: options?.tags,
        }),
    });
}

export async function uploadReviewDocument(
    reviewId: string,
    file: File,
    options?: {
        projectId?: string;
        documentIds?: string[];
        columnsConfig?: { index: number; name: string; prompt: string }[];
    },
): Promise<AuditFlowDocument> {
    const uploaded = options?.projectId
        ? await uploadProjectDocument(options.projectId, file)
        : await uploadStandaloneDocument(file);

    await updateTabularReview(reviewId, {
        columns_config: options?.columnsConfig,
        document_ids: [...(options?.documentIds ?? []), uploaded.id],
    });

    return uploaded;
}

export async function deleteTabularReview(reviewId: string): Promise<void> {
    await apiRequest(`/tabular-review/${reviewId}`, { method: "DELETE" });
}

export interface TabularGenerationOptions {
    documentIds?: string[];
    columnIndices?: number[];
    force?: boolean;
}

export async function streamTabularGeneration(
    reviewId: string,
    options?: TabularGenerationOptions,
): Promise<Response> {
    const authHeaders = await getAuthHeader();
    const body =
        options &&
        (options.documentIds?.length ||
            options.columnIndices?.length ||
            options.force)
            ? {
                  ...(options.documentIds?.length
                      ? { document_ids: options.documentIds }
                      : {}),
                  ...(options.columnIndices?.length
                      ? { column_indices: options.columnIndices }
                      : {}),
                  ...(options.force ? { force: true } : {}),
              }
            : undefined;
    return fetch(`${API_BASE}/tabular-review/${reviewId}/generate`, {
        method: "POST",
        headers: {
            ...authHeaders,
            ...(body ? { "Content-Type": "application/json" } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
    });
}

export async function streamTabularChat(
    reviewId: string,
    messages: { role: string; content: string }[],
    chat_id?: string | null,
    signal?: AbortSignal,
    context?: { reviewTitle?: string | null; projectName?: string | null },
): Promise<Response> {
    const authHeaders = await getAuthHeader();
    return fetch(`${API_BASE}/tabular-review/${reviewId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
            messages,
            chat_id: chat_id ?? undefined,
            review_title: context?.reviewTitle ?? undefined,
            project_name: context?.projectName ?? undefined,
        }),
        signal: signal ?? undefined,
    });
}

export interface TRCitationAnnotation {
    type: "tabular_citation";
    ref: number;
    col_index: number;
    row_index: number;
    col_name: string;
    doc_name: string;
    quote: string;
}

interface RawTRMessage {
    id: string;
    chat_id: string;
    role: "user" | "assistant";
    content: string | AssistantEvent[] | null;
    annotations?: TRCitationAnnotation[] | null;
    created_at: string;
}

export interface TRDisplayMessage {
    role: "user" | "assistant";
    content: string;
    events?: AssistantEvent[];
    annotations?: TRCitationAnnotation[];
}

export interface TRChat {
    id: string;
    title: string | null;
    created_at: string;
    updated_at: string;
}

export function mapTRMessages(raw: RawTRMessage[]): TRDisplayMessage[] {
    return raw.map((m) => {
        if (m.role === "user") {
            return {
                role: "user" as const,
                content: typeof m.content === "string" ? m.content : "",
            };
        }
        const events = Array.isArray(m.content)
            ? (m.content as AssistantEvent[])
            : undefined;
        const content =
            events
                ?.filter((e) => e.type === "content")
                .map((e) => (e as { type: "content"; text: string }).text)
                .join("") ?? "";
        return {
            role: "assistant" as const,
            content,
            events,
            annotations: m.annotations ?? undefined,
        };
    });
}

export async function getTabularChats(reviewId: string): Promise<TRChat[]> {
    return apiRequest<TRChat[]>(`/tabular-review/${reviewId}/chats`);
}

export async function getTabularChatMessages(
    reviewId: string,
    chatId: string,
): Promise<RawTRMessage[]> {
    return apiRequest<RawTRMessage[]>(
        `/tabular-review/${reviewId}/chats/${chatId}/messages`,
    );
}

export async function deleteTabularChat(
    reviewId: string,
    chatId: string,
): Promise<void> {
    await apiRequest(`/tabular-review/${reviewId}/chats/${chatId}`, {
        method: "DELETE",
    });
}

export async function regenerateTabularCell(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    modelOverride?: string,
): Promise<{
    summary: string;
    flag: "green" | "grey" | "yellow" | "red";
    reasoning: string;
}> {
    return apiRequest(`/tabular-review/${reviewId}/regenerate-cell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            document_id: documentId,
            column_index: columnIndex,
            ...(modelOverride ? { model: modelOverride } : {}),
        }),
    });
}

export async function clearTabularCells(
    reviewId: string,
    documentIds: string[],
): Promise<void> {
    await apiRequest(`/tabular-review/${reviewId}/clear-cells`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_ids: documentIds }),
    });
}

export async function updateTabularCellContent(
    reviewId: string,
    cellId: string,
    content: {
        summary: string;
        flag?: "green" | "grey" | "yellow" | "red";
        reasoning?: string;
    } | null,
): Promise<void> {
    await apiRequest(`/tabular-review/${reviewId}/cells/${cellId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
    });
}

export async function updateTabularCellComment(
    reviewId: string,
    cellId: string,
    comment: string | null,
): Promise<void> {
    await apiRequest(`/tabular-review/${reviewId}/cells/${cellId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
    });
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

type WorkflowType = AuditFlowWorkflow["type"];

export async function listWorkflows(
    type: WorkflowType,
): Promise<AuditFlowWorkflow[]> {
    return apiRequest<AuditFlowWorkflow[]>(`/workflows?type=${type}`);
}

export async function getWorkflow(workflowId: string): Promise<AuditFlowWorkflow> {
    return apiRequest<AuditFlowWorkflow>(`/workflows/${workflowId}`);
}

export async function createWorkflow(payload: {
    title: string;
    type: "assistant" | "tabular";
    prompt_md?: string;
    columns_config?: { index: number; name: string; prompt: string }[];
    practice?: string | null;
}): Promise<AuditFlowWorkflow> {
    return apiRequest<AuditFlowWorkflow>("/workflows", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function bulkCreateWorkflows(workflows: {
    title: string;
    type: "assistant" | "tabular";
    prompt_md?: string;
    columns_config?: { index: number; name: string; prompt: string; format?: string; tags?: string[] }[];
    practice?: string | null;
}[]): Promise<{ created: AuditFlowWorkflow[]; errors: { index: number; title: string; error: string }[] }> {
    return apiRequest("/workflows/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workflows }),
    });
}

export async function updateWorkflow(
    workflowId: string,
    payload: {
        title?: string;
        prompt_md?: string;
        columns_config?: { index: number; name: string; prompt: string }[];
        practice?: string | null;
    },
): Promise<AuditFlowWorkflow> {
    return apiRequest<AuditFlowWorkflow>(`/workflows/${workflowId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function deleteWorkflow(workflowId: string): Promise<void> {
    await apiRequest(`/workflows/${workflowId}`, { method: "DELETE" });
}

export async function listHiddenWorkflows(): Promise<string[]> {
    return apiRequest<string[]>("/workflows/hidden");
}

export async function hideWorkflow(workflowId: string): Promise<void> {
    await apiRequest("/workflows/hidden", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workflow_id: workflowId }),
    });
}

export async function unhideWorkflow(workflowId: string): Promise<void> {
    await apiRequest(`/workflows/hidden/${workflowId}`, { method: "DELETE" });
}

export async function shareWorkflow(
    workflowId: string,
    payload: { emails: string[]; allow_edit: boolean },
): Promise<void> {
    await apiRequest<void>(`/workflows/${workflowId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function listWorkflowShares(workflowId: string): Promise<
    {
        id: string;
        shared_with_email: string;
        allow_edit: boolean;
        created_at: string;
    }[]
> {
    return apiRequest(`/workflows/${workflowId}/shares`);
}

export async function deleteWorkflowShare(
    workflowId: string,
    shareId: string,
): Promise<void> {
    await apiRequest(`/workflows/${workflowId}/shares/${shareId}`, {
        method: "DELETE",
    });
}

// ---------------------------------------------------------------------------
// LAURA
// ---------------------------------------------------------------------------

export async function listLauraSAIProfiles(): Promise<LauraSAIProfile[]> {
    return apiRequest<LauraSAIProfile[]>("/laura/sai-profiles");
}

export async function createLauraSAIProfile(payload: {
    name: string;
    default_language?: string;
    core_instructions_override?: string;
}): Promise<LauraSAIProfile> {
    return apiRequest<LauraSAIProfile>("/laura/sai-profiles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function updateLauraSAIProfile(
    profileId: string,
    payload: Partial<{
        name: string;
        default_language: string;
        core_instructions_override: string | null;
        documents: { slot: string; document_id: string }[];
    }>,
): Promise<LauraSAIProfile> {
    return apiRequest<LauraSAIProfile>(`/laura/sai-profiles/${profileId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function listLauraAudits(projectId?: string): Promise<LauraAudit[]> {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
    return apiRequest<LauraAudit[]>(`/laura${qs}`);
}

export async function createLauraAudit(payload: {
    title: string;
    sai_profile_id?: string;
    output_language?: string;
    project_id: string;
}): Promise<LauraAudit> {
    return apiRequest<LauraAudit>("/laura", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function getLauraAudit(auditId: string): Promise<LauraAudit> {
    return apiRequest<LauraAudit>(`/laura/${auditId}`);
}

export async function updateLauraAudit(
    auditId: string,
    payload: Partial<{
        title: string;
        shared_with: string[];
        current_stage: LauraStageId;
    }>,
): Promise<LauraAudit> {
    return apiRequest<LauraAudit>(`/laura/${auditId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function deleteLauraAudit(auditId: string): Promise<void> {
    await apiRequest(`/laura/${auditId}`, { method: "DELETE" });
}

export async function updateLauraDeliverable(
    auditId: string,
    stage: LauraStageId,
    content_md: string,
): Promise<LauraDeliverable> {
    return apiRequest<LauraDeliverable>(
        `/laura/${auditId}/deliverables/${stage}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content_md }),
        },
    );
}

export async function approveLauraStage(
    auditId: string,
    stage: LauraStageId,
): Promise<{
    audit: LauraAudit;
    stage_completions: LauraAudit["stage_completions"];
    advanced_to: LauraStageId | null;
    approved_by_display?: string | null;
}> {
    return apiRequest(`/laura/${auditId}/stages/${stage}/approve`, {
        method: "POST",
    });
}

export async function getLauraP0State(auditId: string): Promise<LauraP0State> {
    return apiRequest(`/laura/${auditId}/p0`);
}

export async function patchLauraP0Context(
    auditId: string,
    payload: { initial_comments: string; confirm_invalidate?: boolean },
): Promise<{ p0: LauraP0Context }> {
    return apiRequest(`/laura/${auditId}/p0/context`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function invalidateLauraP0(auditId: string): Promise<{ p0: LauraP0Context }> {
    return apiRequest(`/laura/${auditId}/p0/invalidate`, { method: "POST" });
}

export async function generateLauraP0Questions(auditId: string): Promise<{
    p0: LauraP0Context;
    questions: LauraP0Question[];
}> {
    return apiRequest(`/laura/${auditId}/p0/generate-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
    });
}

export async function patchLauraP0Answers(
    auditId: string,
    answers: Record<string, string>,
): Promise<{ p0: LauraP0Context; all_required_filled: boolean }> {
    return apiRequest(`/laura/${auditId}/p0/answers`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
    });
}

export async function generateLauraP0Briefing(auditId: string): Promise<{
    p0: LauraP0Context;
    briefing_md: string;
}> {
    return apiRequest(`/laura/${auditId}/p0/generate-briefing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
    });
}

export async function patchLauraP0Validation(
    auditId: string,
    item_id: string,
    checked: boolean,
): Promise<{ p0: LauraP0Context; all_checked: boolean }> {
    return apiRequest(`/laura/${auditId}/p0/validation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id, checked }),
    });
}

export type LauraStageValidationItem = {
    id: string;
    text: string;
    checked: boolean;
    checked_at?: string;
};

export type LauraStageContext<TForm extends Record<string, unknown> = Record<string, unknown>> = {
    version: number;
    status: "draft" | "generated" | "approved";
    form_data: TForm;
    validation_items?: LauraStageValidationItem[];
    generated_at?: string;
};

export type LauraStageState<TForm extends Record<string, unknown> = Record<string, unknown>> = {
    stage: LauraStageContext<TForm>;
    deliverable: LauraDeliverable | null;
    completion: LauraStageCompletion | null;
    approved_by_display: string | null;
};

const PLANNING_STAGES = ["p1", "p2", "p3", "p4", "p5", "p6", "p7"] as const;
export type LauraPlanningStageId = (typeof PLANNING_STAGES)[number];

export async function getLauraStageState<TForm extends Record<string, unknown>>(
    auditId: string,
    stage: LauraPlanningStageId | LauraStageId,
): Promise<LauraStageState<TForm>> {
    return apiRequest(`/laura/${auditId}/${stage}`);
}

export async function patchLauraStageContext<TForm extends Record<string, unknown>>(
    auditId: string,
    stage: LauraPlanningStageId | LauraStageId,
    payload: {
        form_data?: Partial<TForm>;
        validation_items?: LauraStageValidationItem[];
        status?: "draft" | "generated";
    },
): Promise<{ stage: LauraStageContext<TForm> }> {
    return apiRequest(`/laura/${auditId}/${stage}/context`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function generateLauraStage<TForm extends Record<string, unknown>>(
    auditId: string,
    stage: LauraPlanningStageId | LauraStageId,
): Promise<{
    stage: LauraStageContext<TForm>;
    content_md: string;
    validation_items: LauraStageValidationItem[];
}> {
    return apiRequest(`/laura/${auditId}/${stage}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
    });
}

export async function patchLauraStageValidation(
    auditId: string,
    stage: LauraPlanningStageId | LauraStageId,
    item_id: string,
    checked: boolean,
): Promise<{ stage: LauraStageContext; all_checked: boolean }> {
    return apiRequest(`/laura/${auditId}/${stage}/validation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id, checked }),
    });
}

export async function unlinkLauraAuditDocument(
    auditId: string,
    documentId: string,
    confirmInvalidate?: boolean,
): Promise<void> {
    const qs = confirmInvalidate ? "?confirm_invalidate=true" : "";
    await apiRequest(`/laura/${auditId}/documents/${documentId}${qs}`, {
        method: "DELETE",
    });
}

export async function listLauraAuditDocuments(
    auditId: string,
): Promise<LauraAuditDocument[]> {
    return apiRequest<LauraAuditDocument[]>(`/laura/${auditId}/documents`);
}

export async function linkLauraAuditDocument(
    auditId: string,
    payload: {
        document_id: string;
        role?: string;
        confirm_invalidate?: boolean;
    },
): Promise<LauraAuditDocument> {
    return apiRequest<LauraAuditDocument>(`/laura/${auditId}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function uploadLauraAuditDocument(
    auditId: string,
    file: File,
    role?: string,
): Promise<LauraAuditDocument> {
    const authHeaders = await getAuthHeader();
    const form = new FormData();
    form.append("file", file);
    if (role) form.append("role", role);
    const res = await fetch(`${API_BASE}/laura/${auditId}/documents`, {
        method: "POST",
        headers: authHeaders,
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail ?? res.statusText);
    }
    return res.json();
}

export async function getLauraChatMessages(auditId: string): Promise<
    {
        id: string;
        role: string;
        content: { type: string; text?: string }[] | null;
        created_at: string;
    }[]
> {
    return apiRequest(`/laura/${auditId}/chat/messages`);
}

export async function streamLauraChat(payload: {
    auditId: string;
    messages: { role: string; content: string }[];
    model?: string;
    signal?: AbortSignal;
}): Promise<Response> {
    const { auditId, signal, ...body } = payload;
    const authHeaders = await getAuthHeader();
    return fetch(`${API_BASE}/laura/${auditId}/chat`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...authHeaders,
        },
        body: JSON.stringify(body),
        signal,
    });
}
